/**
 * Free charm, unlocked by an email address.
 *
 * Rendering and eligibility live in snippets/cart-free-gift.liquid - this file only
 * mutates the cart and lets the theme re-render. Keeping the math server-side means
 * the module can never disagree with what Liquid decided.
 *
 * The gift line is marked with the hidden line item property `_free_gift`.
 * Pricing to $0 is handled by the "Free charm over $75 (function)" app discount,
 * which re-reads the same cart attribute this file writes.
 */

const UNLOCK_ATTRIBUTE = '_gift_unlocked';
const SUBSCRIBED_COOKIE = 'ic_popup_subscribed';

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
}

class MFreeGift extends HTMLElement {
  constructor() {
    super();

    this.rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.cartDrawer = document.querySelector('m-cart-drawer');
    this.isCartPage = document.body.classList.contains('template-cart');
    this.busy = false;

    this.giftLineKey = this.dataset.giftLineKey || '';

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    /**
     * Heal before removing.
     *
     * A shopper who signed up last week arrives with the cookie but a brand new
     * cart, so Liquid sees no attribute and renders the signup form. Writing the
     * attribute back is the correct repair, and it has to happen BEFORE the
     * removal branch below or a returning shopper would watch their charm get
     * taken out of the cart they just rebuilt.
     */
    if (this.shouldHeal()) {
      this.healUnlock();
      return;
    }

    // Cart no longer earns the gift but a gift line is still in it.
    if (this.dataset.shouldRemove === 'true' && this.giftLineKey) {
      this.removeGift();
    }
  }

  /**
   * Only heal a cart that is missing the attribute for a shopper we already know
   * is subscribed. Order history stopped mattering on 6 September 2026, so a
   * returning buyer who signed up heals exactly like anyone else.
   */
  shouldHeal() {
    if (window.__mFreeGiftHealed) return false;
    if (this.dataset.unlocked === 'true') return false;
    return Boolean(readCookie(SUBSCRIBED_COOKIE));
  }

  healUnlock() {
    window.__mFreeGiftHealed = true;
    this.toggleLoading(true);
    this.setUnlockAttribute();
  }

  onClick(event) {
    const addButton = event.target.closest('[data-free-gift-add]');
    if (addButton) {
      event.preventDefault();
      this.addGift();
      return;
    }

    const changeButton = event.target.closest('[data-free-gift-change]');
    if (changeButton && changeButton.tagName === 'BUTTON') {
      event.preventDefault();
      this.removeGift();
      return;
    }

    const signupButton = event.target.closest('[data-free-gift-signup]');
    if (signupButton) {
      event.preventDefault();
      this.signup();
    }
  }

  // Enter in the email field should unlock, not submit the surrounding cart form.
  onKeydown(event) {
    if (event.key !== 'Enter') return;
    if (!event.target.closest('[data-free-gift-email]')) return;

    event.preventDefault();
    this.signup();
  }

  /**
   * Inline signup: same three steps as the popup, so a shopper who dismissed the
   * popup and got here instead ends up in exactly the same state.
   */
  signup() {
    const input = this.querySelector('[data-free-gift-email]');
    const error = this.querySelector('[data-free-gift-error]');
    if (!input || this.busy) return;

    const email = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (error) error.hidden = false;
      input.focus();
      return;
    }
    if (error) error.hidden = true;

    this.busy = true;
    this.toggleLoading(true);

    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', email);
    formData.append('contact[tags]', 'newsletter,cart,free-charm');

    /**
     * Set the popup's own cookie here too.
     *
     * Without it the popup counts this shopper as a stranger and opens over the
     * drawer a few seconds later, asking for the email they just gave. It also
     * lands on top of the charm picker and eats the click on "Add it".
     */
    writeCookie(SUBSCRIBED_COOKIE, '1', 365);

    Promise.all([
      fetch('/contact', { method: 'POST', body: formData, headers: { Accept: 'application/json' } })
        .catch(() => {
          /* Shopify answers 200 even for a duplicate, so this is a network fault.
             Unlock anyway rather than making them type it twice. */
        }),
      this.subscribe(email),
    ])
      .then(() => {
        if (window._learnq) {
          window._learnq.push(['identify', { $email: email }]);
          window._learnq.push(['track', 'Popup Signup', { offer: 'free_charm', source: 'cart' }]);
        }
      })
      .catch(() => {})
      .then(() => this.setUnlockAttribute());
  }

  /**
   * Actually subscribe the address.
   *
   * WHY THIS EXISTS. Everything above this line stamps a record without granting
   * consent. `identify` only creates a Klaviyo profile - the popup's own note
   * says so, and it is why roughly 82% of the addresses that popup collected
   * between 21 May and 15 September 2026 sat unreachable. The POST to /contact
   * does not help either: Shopify records those customers with no marketing
   * consent, and it silently drops the `contact[tags]` we send, so they cannot
   * even be segmented. Verified on 2026-09-22 with a test address submitted
   * through this capture: Klaviyo profile created, NEVER_SUBSCRIBED, on no list;
   * Shopify customer created with an empty tag list.
   *
   * The popup got this call on 15 September. This module did not, and kept
   * collecting addresses nobody could email. This closes that gap.
   *
   * Two traps, both learned the hard way in the popup:
   *
   *  - The body shape is the CLIENT endpoint's, not the server one. It takes the
   *    address and nothing else. Sending the server shape's `subscriptions`
   *    block is rejected 400 with "'subscriptions' is not a valid field for the
   *    resource 'profile'" - and because this call fails open, that 400 would be
   *    swallowed and the bug would look exactly like the one this fixes.
   *  - Consent on this endpoint is implied by the request. There is no flag.
   *
   * `custom_source` is deliberately different from the popup's. Until now there
   * was no way to tell a cart-drawer signup from a popup one after the fact,
   * which is exactly why the backfill population had to be guessed at.
   *
   * It fails open, like every other network call in this file. A shopper who
   * typed their address gets the charm whether or not Klaviyo answers.
   */
  subscribe(email) {
    const company = this.dataset.klaviyoCompany;
    const list = this.dataset.klaviyoList;
    if (!company || !list) return Promise.resolve();

    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Cart drawer - free charm',
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
      }
    ).catch(function () { /* fail open: never hold the charm on this */ });
  }

  setUnlockAttribute() {
    const attributes = {};
    attributes[UNLOCK_ATTRIBUTE] = 'true';

    this.busy = true;
    return this.request(`${this.rootUrl}cart/update.js`, { attributes });
  }

  addGift() {
    const select = this.querySelector('[data-free-gift-select]');
    if (!select || !select.value || this.busy) return;

    this.toggleLoading(true);

    this.request(`${this.rootUrl}cart/add.js`, {
      items: [
        {
          id: Number(select.value),
          quantity: 1,
          properties: { _free_gift: 'true' },
        },
      ],
    });
  }

  removeGift() {
    if (!this.giftLineKey || this.busy) return;

    this.toggleLoading(true);

    this.request(`${this.rootUrl}cart/change.js`, {
      id: this.giftLineKey,
      quantity: 0,
    });
  }

  request(url, body) {
    this.busy = true;

    return fetch(url, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then(() => this.refreshCart())
      .catch(() => {
        this.busy = false;
        this.toggleLoading(false);
      });
  }

  /**
   * Mirrors gift-wrapping.js: full reload on the cart page, in-place refresh in the
   * drawer. Both paths re-render cart-free-gift.liquid, so state is always
   * recomputed server-side.
   */
  refreshCart() {
    if (this.isCartPage || !this.cartDrawer) {
      window.location.href = (window.MinimogSettings && window.MinimogSettings.routes.cart) || `${this.rootUrl}cart`;
      return;
    }

    return fetch(`${this.rootUrl}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.cartDrawer.onCartDrawerUpdate();
        this.cartDrawer.classList.toggle('m-cart--empty', cart.item_count === 0);

        if (window.MinimogEvents && window.MinimogTheme) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, cart);
        }
      })
      .finally(() => {
        this.busy = false;
        this.toggleLoading(false);
      });
  }

  toggleLoading(state) {
    this.classList.toggle('m-free-gift--loading', Boolean(state));
  }
}

customElements.define('m-free-gift', MFreeGift);

/**
 * Keep the drawer copy in sync.
 *
 * m-cart-drawer's onCartDrawerUpdate() only re-renders [data-minimog-cart-items],
 * [data-minimog-cart-discounts], [data-cart-subtotal] and [data-minimog-gift-wrapping].
 * This module sits in the drawer header, which is never refreshed - so without this it
 * would show whatever state the page was loaded with. (The cart page is fine: mutations
 * there do a full reload.)
 *
 * Subscribed once globally rather than per-element, since refreshing replaces the
 * element and a per-element subscription would leak on every cart update.
 */
(function syncDrawerCopy() {
  if (window.__mFreeGiftDrawerSync) return;
  window.__mFreeGiftDrawerSync = true;

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let refreshing = false;

  /**
   * Put the shopper's own typing back after the module is re-rendered.
   *
   * Only restores a charm value the fresh markup actually offers, so a variant that
   * sold out between renders falls through to the server's default instead of a dead
   * option. The half-typed email is restored unconditionally, since losing it mid-word
   * is what makes people give up on the field.
   */
  function restoreInput(root, chosen, typedEmail) {
    const select = root.querySelector('[data-free-gift-select]');
    if (select && chosen && Array.from(select.options).some((option) => option.value === chosen)) {
      select.value = chosen;
    }

    const email = root.querySelector('[data-free-gift-email]');
    if (email && typedEmail) email.value = typedEmail;
  }

  async function refreshDrawerRoot() {
    const root = document.querySelector('m-cart-drawer [data-free-gift-root]');
    if (!root || refreshing) return;

    refreshing = true;
    try {
      const response = await fetch(`${rootUrl}?section_id=cart-drawer`);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('[data-free-gift-root]');

      /**
       * Read what the shopper has entered AFTER the fetch resolves, not before it
       * starts.
       *
       * The fetch takes a few hundred ms and shoppers pick inside that window.
       * Without this, any cart update between choosing a charm and pressing "Add it"
       * reset the picker to its first option - and that first option is what shipped,
       * whatever they had actually selected.
       */
      const currentSelect = root.querySelector('[data-free-gift-select]');
      const chosen = currentSelect ? currentSelect.value : '';
      const currentEmail = root.querySelector('[data-free-gift-email]');
      const typedEmail = currentEmail ? currentEmail.value : '';

      // innerHTML (not replaceWith) so the anchor survives and can repopulate later.
      root.innerHTML = fresh ? fresh.innerHTML : '';
      restoreInput(root, chosen, typedEmail);
    } catch (error) {
      /* leave the stale render in place rather than blanking the module */
    } finally {
      refreshing = false;
    }
  }

  function subscribe() {
    if (!window.MinimogEvents || !window.MinimogTheme) return false;
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, refreshDrawerRoot);
    return true;
  }

  if (!subscribe()) {
    document.addEventListener('DOMContentLoaded', subscribe, { once: true });
  }
})();
